import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAlertTypeById,
  createAlertType,
  updateAlertType,
  uploadAlertTypeFile,
  deleteAlertType,
  restoreAlertType,
  getAlertTypes,
  type AlertTypeFilters,
  type AlertType,
} from '../../services';

export const alertTypeKeys = {
  all: ['alertTypes'] as const,
  lists: () => [...alertTypeKeys.all, 'list'] as const,
  list: (filters: AlertTypeFilters) =>
    [...alertTypeKeys.lists(), { ...filters }] as const,
  details: () => [...alertTypeKeys.all, 'detail'] as const,
  detail: (id: number) => [...alertTypeKeys.details(), id] as const,
};

export function useAlertTypes(filters: AlertTypeFilters) {
  return useQuery({
    queryKey: alertTypeKeys.list(filters),
    queryFn: () => getAlertTypes(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useAlertType(id: number) {
  return useQuery({
    queryKey: alertTypeKeys.detail(id),
    queryFn: () => getAlertTypeById(id),
    enabled: !!id,
  });
}

export function useCreateAlertType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAlertType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.lists() });
    },
  });
}

export function useUpdateAlertType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AlertType> }) =>
      updateAlertType(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.detail(id) });
    },
  });
}

export function useUploadAlertTypeFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, formData }: { id: number; formData: FormData }) =>
      uploadAlertTypeFile(id, formData),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.detail(id) });
    },
  });
}

export function useDeleteAlertType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAlertType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.lists() });
    },
  });
}

export function useRestoreAlertType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: restoreAlertType,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: alertTypeKeys.detail(id) });
    },
  });
}
